// Sınırsız (unlimited) katalog önbelleği.
//
// `/v2/bundles` tekil bir bundle ucu SUNMAZ — yalnızca filtrelenebilir liste
// vardır (src/FRONTEND_INTEGRATION.md §4). Bu yüzden hem "Sınırsız" sekmesi hem
// de bundle detay sayfası aynı listeden beslenir ve liste burada bir kez
// çekilip paylaşılır (bugün 37 bundle, sayfalama yok).
//
// İkinci kullanıcı: hesap ekranı. Satın alınmış bir siparişin sınırsız olup
// olmadığına `clientProductId` → `plan.isUnlimited` eşlemesiyle karar veriyoruz.
// "ID >= 100000 ise yenidir" sezgisi bilerek KULLANILMIYOR: doküman bu aralığı
// frontend için sözleşme saymıyor, sadece bilgi olarak veriyor (§1).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getBundles } from './homeApi';

const langOf = (lng) => (lng || 'tr').split('-')[0];

// Dil başına tek uçuş: Accept-Language yanıttaki ülke adlarını (nameSelected)
// etkilediği için dil değişince önbellek tazelenmeli.
let cache = { lang: null, promise: null };

export const loadUnlimitedBundles = () => {
  const lang = langOf(i18n.language);
  if (cache.lang !== lang || !cache.promise) {
    const promise = getBundles({ family: 'unlimited' }).catch((error) => {
      // Başarısız isteği önbellekte bırakma: kullanıcı "tekrar dene" dediğinde
      // aynı hatayı sonsuza dek geri vermesin.
      if (cache.promise === promise) cache = { lang: null, promise: null };
      throw error;
    });
    cache = { lang, promise };
  }
  return cache.promise;
};

export const resetUnlimitedBundles = () => {
  cache = { lang: null, promise: null };
};

// Liste yalnızca id ile aranabilir; route parametresi string gelir.
export const findUnlimitedBundle = (bundles, bundleId) =>
  (bundles || []).find((b) => String(b.id) === String(bundleId)) || null;

// Web'den satılabilir planlar: `priceId` yoksa plan henüz fiyatlanmamıştır ve
// PayPal siparişi açılamaz (§6). sortOrder listeleme sırasının tek kaynağıdır —
// sınırsız planlarda veri miktarı hepsinde eşit olduğu için ona göre sıralanmaz.
export const sellablePlans = (bundle) =>
  [...(bundle?.plans || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

export const isSellable = (plan) => Boolean(plan?.priceId);

const planIdSet = (bundles) => {
  const set = new Set();
  (bundles || []).forEach((b) =>
    (b.plans || []).forEach((p) => {
      if (p.isUnlimited) set.add(String(p.id));
    })
  );
  return set;
};

/**
 * Sınırsız plan kimlikleri. Yüklenene kadar `null` döner — çağıran taraf bu
 * sürede sınırsız uyarlaması YAPMAMALI (yanlışlıkla veri çubuğu gizlenmesin).
 * Katalog çekilemezse boş küme döner: eski davranış korunur.
 */
export const useUnlimitedPlanIds = () => {
  const { i18n: i18next } = useTranslation();
  const [ids, setIds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadUnlimitedBundles()
      .then((list) => !cancelled && setIds(planIdSet(list)))
      .catch(() => !cancelled && setIds(new Set()));
    return () => {
      cancelled = true;
    };
  }, [i18next.language]);

  return ids;
};

// Bir sipariş satırı sınırsız pakete mi ait? `ids` yüklenmemişse false.
export const isUnlimitedOrder = (ids, esim) =>
  Boolean(ids && esim?.clientProductId != null && ids.has(String(esim.clientProductId)));
